import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormWrapper} from "../items/form.wrapper";
import {ReactiveFormsModule} from "@angular/forms";

import {FormFieldErrorComponent} from "../form-field-error/form-field-error.component";
import {AbstractFormFieldComponent} from "../abstract-form-field.component";
import {FormLabelComponent} from "../form-label/form-label.component";
import {FormInputConfig} from "./form-input-config";
import {FormStateEnum} from "../items/form-state.enum";
import {distinctUntilChanged, merge, Subscription} from "rxjs";
import {isDefined, nonBlank, unsubscribe} from "../../utils/epic.utils";
import {CopyClipboardComponent} from "../../copy-clipboard/copy-clipboard.component";
import {Size} from "../../../base/data/size";

@Component({
  selector: 'acc-form-input',
  imports: [
    ReactiveFormsModule,
    FormFieldErrorComponent,
    FormLabelComponent,
    CopyClipboardComponent
  ],
  templateUrl: './form-input.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-input.component.css'
})
export class FormInputComponent extends AbstractFormFieldComponent implements OnInit, OnDestroy {

  @ViewChild('input')
  public input: ElementRef | undefined;

  @Input({required: true})
  public name!: string;

  @Input({required: true})
  public wrapper!: FormWrapper;

  @Input()
  public type: 'text' | 'number' | 'password' = 'text';

  @Input()
  public config: FormInputConfig = new FormInputConfig();

  // Integer-only field: strips thousands separators / non-digits before the value reaches the control
  // (a native type="number" treats "." as decimal, so "128.400" would otherwise be sent verbatim to a Long)
  @Input()
  public integer: boolean = false;

  @Output()
  public readonly accKeytap: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public readonly accInputChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public readonly accChange: EventEmitter<string> = new EventEmitter<string>();

  protected currentValue: string | undefined;

  private readonly subs: Subscription[] = [];

  private keyTimeout: any;

  constructor() {
    super();
    this.subs.push(merge(this.accKeytap, this.accInputChange)
      .pipe(distinctUntilChanged())
      .subscribe((value) => {
        this.accChange.emit(value);
        this.currentValue = value;
      }));
  }

  ngOnInit(): void {
    this.subs.push(this.wrapper.onReady().subscribe(() => {
      if (this.wrapper.hasControl(this.name)) {
        this.currentValue = this.wrapper.getValue(this.name);
      }
      if (this.isViewOnly() || this.isDisabled()) {
        this.subs.push(this.wrapper.onChangeByNameDistinctValue(this.name).subscribe((vc) => this.currentValue = vc.getValue()));
      }
    }));
  }

  ngOnDestroy(): void {
    clearTimeout(this.keyTimeout);
    unsubscribe(this.subs);
  }

  protected override isViewOnly(): boolean {
    return this._isViewOnly(this.name, this.wrapper);
  }

  protected override getSize(): Size | undefined {
    return this.config.size;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/explicit-module-boundary-types
  protected override afterValueChanged(_name: string, newValue: any, _oldValue: any, _initialValue: boolean): void {
    this.accInputChange.emit(newValue);
  }

  protected onInputChange(event: Event): void {
    clearTimeout(this.keyTimeout);
    this.sanitizeInteger(event);
    this._onChange(this.name, this.wrapper, event);
  }

  // Removes thousands separators and any non-digit so e.g. "128.400" -> "128400" for Long fields
  private sanitizeInteger(event: Event): void {
    if (this.integer && event.target instanceof HTMLInputElement) {
      event.target.value = event.target.value.replace(/[^\d-]/g, '');
    }
  }

  protected onInputKeyEvent(event: Event): void {
    clearTimeout(this.keyTimeout);
    this.keyTimeout = setTimeout(() => this.onInputChange(event), this.getKeyDelay());
    this.subs.push(this.keyTimeout);
    if (this.wrapper.isReady && event.target instanceof HTMLInputElement) {
      if (this.config.uppercase) {
        event.target.value = event.target.value.toUpperCase();
      }
      this.accKeytap.emit(event.target.value);
    }
    this._updateState(this.name, this.wrapper, FormStateEnum.CHANGED);
  }

  protected isInvalid(): boolean {
    return this._isInvalid(this.name, this.wrapper);
  }

  protected getClass(): string {
    const size: Size | undefined = this.getSize();
    return isDefined(size) ? 'form-control-' + size.value : '';
  }

  protected getPlaceholder(): string {
    if (isDefined(this.config.placeholder) && nonBlank(this.config.placeholder)) {
      return this.i18n.translate(this.config.placeholder);
    }
    return '';
  }

  protected isDisabled(): boolean {
    return this._isDisabled(this.name, this.wrapper);
  }

  private getKeyDelay(): number {
    if (isDefined(this.config.keyDelay)) {
      return this.config.keyDelay;
    }
    return this.wrapper.getChangeDelayTime();
  }
}
